import { useThrottleFn } from 'ahooks';
import { useEffect, useState } from 'react';

type Props = {
  /** 需要监控的DOM节点 */
  dom: HTMLElement;
  /** 监听左右滑动的回调 */
  onScroll?: (event: Event) => void;
  /** 左右边缘的安全距离，默认5px */
  edgeDistance?: number;
};

type ReturnType = {
  /** 内部内容是否超出容器宽度 */
  isOverflow: boolean;
  /** （左按钮显示隐藏）是否能继续向左⬅️滑动 */
  allowLeftScroll: boolean;
  /** （右按钮显示隐藏）是否能继续向右➡️滑动 */
  allowRightScroll: boolean;
  /** （点击左按钮）向左滑动距离为x的方法，传入滑动距离，比如clientWidth/3 */
  scrollToLeft: (scrollWidth: number) => void;
  /** （点击右按钮）向右滑动距离为x的方法，传入滑动距离，比如clientWidth/3 */
  scrollToRight: (scrollWidth: number) => void;
  /** （比如监听resize时候，DOM节点的宽度变化可使用）重新刷新计算内部滑动信息 */
  reCalcReachedEdge: () => void;
  /** DOM节点滑动方法，scrollLeft */
  updateScroll: (scrollLeft: number) => void;
};

/**
 * 处理宽度超出，需要滑动的组件
 * @param dom 需要监控的DOM节点
 * @param onScroll 滑动回调
 * @param edgeDistance 默认5px。 左右边缘的安全兼容距离（左右的安全边缘距离）
 * @returns {boolean} isOverflow - 内部内容是否超出容器宽度
 * @returns {boolean} allowLeftScroll - （左按钮显示隐藏）是否能继续向左滑动
 * @returns {boolean} allowRightScroll - （右按钮显示隐藏）是否能继续向右滑动
 * @returns {(number) => void} scrollToLeft - （点击左按钮滑动距离为x）向左滑动的方法
 * @returns {(number) => void} scrollToRight - （点击右按钮滑动距离为x）向右滑动的方法
 * @returns {(number) => void} updateScroll - （DOM节点滑动），比如点击子节点滑动聚焦到子节点位置时，直接调用此方法滑动即可
 * @returns {() => void} reCalcReachedEdge - （比如监听resize时候，DOM节点的宽度变化可使用）重新计算并更新内部的滑动信息
 *  */
export const useWidthTransition = ({ dom, onScroll, edgeDistance = 5 }: Props): ReturnType => {
  const [isOverflow, setIsOverflow] = useState(false);
  const [allowLeftScroll, setAllowLeftScroll] = useState(false);
  const [allowRightScroll, setAllowRightScroll] = useState(false);

  /** 判断当前滑动位置，是否已经到达边缘，赋值内部状态 */
  const { run: reCalcReachedEdge } = useThrottleFn(
    () => {
      const { scrollLeft, scrollWidth, clientWidth } = dom ?? {};
      if (clientWidth < scrollWidth) {
        setIsOverflow(true);
        if (scrollLeft <= edgeDistance) {
          setAllowLeftScroll(false);
          setAllowRightScroll(true);
          return;
        }

        if (scrollLeft > edgeDistance && scrollLeft < scrollWidth - clientWidth - edgeDistance) {
          setAllowLeftScroll(true);
          setAllowRightScroll(true);
          return;
        }

        if (scrollLeft >= scrollWidth - clientWidth - edgeDistance) {
          setAllowLeftScroll(true);
          setAllowRightScroll(false);
          return;
        }
      } else {
        setAllowLeftScroll(false);
        setAllowRightScroll(false);
        setIsOverflow(false);
      }
    },
    { wait: 100, leading: true }
  );

  useEffect(() => {
    if (!dom) return; // console.error('useWidthTransition dom参数为空')

    reCalcReachedEdge();

    const onScrollCb = (e: Event) => {
      reCalcReachedEdge();
      onScroll?.(e);
    };
    dom.addEventListener('scroll', onScrollCb);

    return () => {
      dom.removeEventListener('scroll', onScrollCb);
    };
  }, [dom]);

  const handleDomScroll = (scrollX: number) => {
    try {
      dom.scrollTo({ left: scrollX, top: 0, behavior: 'smooth' });
    } catch (e) {
      dom.scrollTo(scrollX, 0);
      console.error(e);
    }
  };

  const scrollToLeft = (scrollWidth: number) => {
    if (allowLeftScroll) {
      const { scrollLeft } = dom ?? {};
      handleDomScroll(scrollLeft - scrollWidth);
    }
  };

  const scrollToRight = (scrollWidth: number) => {
    if (allowRightScroll) {
      const { scrollLeft } = dom ?? {};
      handleDomScroll(scrollLeft + scrollWidth);
    }
  };

  return {
    isOverflow,
    allowLeftScroll,
    allowRightScroll,
    scrollToRight,
    scrollToLeft,
    updateScroll: handleDomScroll,
    reCalcReachedEdge,
  };
};
